import {
  sortBy, throttle, last, findLastIndex, findIndex, difference, differenceBy,
} from 'lodash';
import sessionStorage from 'sessionstorage';
import sanitizeHtml from 'sanitize-html';
import api from 'src/api';
import settings from 'src/features/settingsByApollo';
import humanFileSize from 'src/filters/humanFileSize';
import {
  C_WEB_ATTACHMENT_MAX_SIZE,
  C_CUSTOMER_LOGIN_ENABLED,
  C_CANCELED_ACTIVITY_WITHOUT_PROCESSING,
  C_MESSAGES_FEED_HISTORY_FOR_CURRENT_CHANNEL_ONLY,
  CHANNEL_NAME,
} from 'src/store/modules/app';
import { focused } from 'src/store/modules/home';
import { SET_CUSTOMER } from 'src/store/modules/customer';
import {
  activity,
  auth,
  channelType,
  commandType,
  consts,
  formattedMessage,
  generateUniqueId,
  isEmpty,
  notice as channels,
  phone,
  toast,
  addItemsType,
} from 'src/utils';
import isTokenValid from 'src/filters/isTokenValid';
import hideCard from 'src/filters/clientCardAll';

import i18n from 'src/features/i18n';
import { BACKEND_HTTP_PATH } from 'src/config';
import { socketEventEmitter } from 'src/utils/notice/subscribe';

function getStateHiddenChat() {
  const isHiddenChat = sessionStorage.getItem('IS_HIDDEN');
  if (isHiddenChat && isHiddenChat === 'true') {
    return true;
  }
  return false;
}

const BUTTONS_MENU = 'buttonsMenu';
const localButtonsMenu = {
  set(buttons, activityId) {
    localStorage.setItem(`${BUTTONS_MENU}_${activityId}`, buttons);
  },

  remove(activityId) {
    localStorage.removeItem(`${BUTTONS_MENU}_${activityId}`);
  },

  get(activityId) {
    return localStorage.getItem(`${BUTTONS_MENU}_${activityId}`);
  },
};

const customerSET = `customer/${SET_CUSTOMER}`;

const stateData = {
  isOpenConversation: false,
  timeFreeOperator: 0,
  page: null,
  url: '',
  activityId: '',
  msgs: [],
  progress: {},
  vkId: '',
  authId: localStorage.getItem('authId') || undefined,
  intent: {},
  operatorIsTyping: false,
  operatorOnline: false,
  onlineAt: undefined,
  customerConnected: null,
  notice: {},
  activityOperator: null,
  avatarUrl: null,
  isRegistered: false,
  readMessageOnly: [],
  isHiddenChat: getStateHiddenChat(),
  cursor: '',
  hasNextPage: false,
  fetching: false,
  errorFetching: false,
  firstMsgUnsend: null,
  oldTitle: '',
  activeTitle: '',
  titleIntervalId: null,
  voteFormMounted: false,
  virtualOperator: false,
  left: false,
  buttonsMenu: [],
  buttonsMenuMsgId: null,
  firstMsg: '',
  clientType: localStorage.getItem('clientType') || null,
  isVisibleWidget: true,
};

const typeRequestType = 'requestType';
const typeCase = 'case';
const typOfRole = 'customer';
const allowedStatuses = ['in_progress', 'pending', 'on_hold'];
const UNAUTHORIZED = 'unauthorized';

const setNotice = (commit, notice) => {
  commit('SET_NOTICE', notice);
  if (notice.type === commandType.SOCKET_ERROR) {
    commit('SET_CONNECTED', false);
  } else {
    commit('SET_CONNECTED', true);
  }
};

function send(activityId, msg) {
  return channels.push(activityId, commandType.NEW_MSG, msg);
}

function setIsRead(activityId, ids) {
  return channels.push(activityId, commandType.IS_READ, { ids });
}

function pushTypingEvt(activityId, value) {
  return channels.push(activityId, commandType.IS_TYPING, { value });
}

function subscribe(data) {
  channels.subscribe(data);
}

function unSubscribe(activityId, channel) {
  channels.unSubscribe(activityId, channel);
}

function isSubscribe(activityId, channel) {
  return channels.isSubscribe(activityId, channel);
}

function disconnect() {
  channels.disconnect();
}

function replyDelivered(activityId) {
  const datetime = (new Date()).toISOString();
  return channels.push(activityId, commandType.REPLY_DELIVERED, { datetime, activityId });
}

async function createActivity(rootState, { id, type }) {
  let activityId = '';
  if (type === typeRequestType) {
    activityId = await api.createActivityByRequestType(id, rootState.app[CHANNEL_NAME]);
  } else if (type === typeCase) {
    activityId = await api.createActivityByCase(id, rootState.app[CHANNEL_NAME]);
  }
  return activityId;
}

async function getActivityData(activityId) {
  const queryResult = await api.getActivityAndCaseMessages(activityId);
  const {
    messages, operator, customer, state, virtualOperator, config, avatarUrl, channel,
  } = queryResult;
  const requestTypeName = queryResult.requestType.name;
  return {
    msgs: messages.map((msg) => formattedMessage(msg, customer && customer.type)),
    operator,
    state,
    requestTypeName,
    virtualOperator,
    config,
    avatarUrl,
    channel,
  };
}

async function onLogin({
  rootState, commit, dispatch, username, intent,
}) {
  commit(customerSET, { username }, { root: true });
  const { id, type } = intent;
  const activityId = await createActivity(rootState, { id, type });
  commit('LOGIN', activityId);
  activity.setId(activityId);
  commit('SET_INTENT', intent);
  dispatch('go', 'demo-chat');
}

async function onLeft({ state, dispatch, commit }, notice) {
  const activityId = activity.getId();
  await dispatch('verificationToken');
  const activityData = await getActivityData(activityId);
  const csiField = activityData.config
    .find((configItem) => configItem.name === 'csi_enable');
  const csiEnable = csiField ? csiField.value : false;
  if (state.left && !csiEnable) return;
  commit('SET_LEFT', true);
  dispatch('clearButtonsMenu');
  const { msgs } = activityData;
  const activityState = activityData.state;
  dispatch('stopAutoChangeTitle', {});
  let lastSender = 'operator';
  let lastIsAuto = false;
  if (last(msgs)) {
    lastSender = (last(msgs).sender === 'info_message')
      ? msgs[findLastIndex(msgs) - 1].sender : last(msgs).sender;
    lastIsAuto = (last(msgs).sender === 'info_message')
      ? msgs[findLastIndex(msgs) - 1].auto : last(msgs).auto;
  }

  // notice.csi === true если активность является csi-активностью
  // (когда приходит left при завершении опроса csi)

  // завершение не-csi-активности, закрытой не-клиентом и не-авто, при включенной оценке csi
  const isNonCsiActivityClosedByOperatorOrBotWhileCsiEnable = csiEnable
    && !notice.csi
    && notice.closedBy !== 'client' && notice.closedBy !== 'auto';

  // завершение не csi-активности по таймауту при включенной оценке csi
  const isNonCsiActivityAutoClosedWhileCsiEnable = csiEnable
  && !notice.csi && notice.closedBy === 'auto'
  && (lastSender !== 'operator' || lastIsAuto); // TODO рефакторинг, возможно это лишнее условие,
  // тк всегда (?) выполняется при других условиях:
  // если последнее сообщение было не от оператора,
  // то перед закрытием по таймауту придет авто-сообщение?
  // ЛИБО дополнить название переменной

  if (isNonCsiActivityClosedByOperatorOrBotWhileCsiEnable) {
    if (notice.needProcessCsi) return; // событие `left` для активности, по которой далее идет оценка csi
    dispatch('unSubscribeWithoutSurvey');
  } else if (notice.closedBy === 'bot') {
    setTimeout(() => {
      dispatch('unSubscribe');
    }, 3000);
  } else if (activityState === 'abandoned'
    || (notice.closedBy === 'auto' && lastSender === 'operator' && !lastIsAuto)) {
    const { needProcessCsi } = notice;
    if (needProcessCsi) {
      return;
    }
    dispatch('unSubscribeWithoutSurvey');
  } else if (isNonCsiActivityAutoClosedWhileCsiEnable) {
    const { needProcessCsi } = notice;
    if (needProcessCsi) return; // событие `left` для активности, по которой далее идет оценка CSI
    dispatch('unSubscribeWithoutSurvey');
  } else {
    dispatch('unSubscribe');
  }
}

function startActivityPage(dispatch, rootState) {
  const noNeedLogin = rootState
    ? rootState.app.C_WEB_AUTO_CLASSIFIER_ENABLE || !rootState.app.C_CUSTOMER_LOGIN_ENABLED
    : true;
  if (noNeedLogin) {
    dispatch('go', 'first-message');
  } else {
    dispatch('go', 'reason-treatment');
  }
}

function onUnableToJoin({
  dispatch, commit, state, rootState,
}) {
  if (state.left) return;
  dispatch('baseUnSubscribe');
  commit('CLEAR_STATE_MSGS_AND_ACTIVITY_ID');
  startActivityPage(dispatch, rootState);
}

async function onTransfer({
  commit, dispatch, rootState,
}, newActivityId, oldActivityId) {
  activity.setId(newActivityId);
  commit('SET_ACTIVITY_ID', newActivityId);
  dispatch('clearButtonsMenu');
  await dispatch('verificationToken');

  await getActivityData(oldActivityId).then(({ virtualOperator, requestTypeName }) => {
    if (!virtualOperator) {
      let bodyMessage = '';
      if (requestTypeName === 'English' || requestTypeName === 'english') {
        bodyMessage = i18n.t('T_SEARCHING_OPERATOR', 'en');
      } else {
        bodyMessage = i18n.t('T_SEARCHING_OPERATOR', 'ru');
      }
      const msg = {
        activityId: oldActivityId,
        body: bodyMessage,
        username: 'СИСТЕМА',
        insertedAt: new Date(),
        sender: 'operator',
      };

      commit('ADD_MESSAGE', msg);
    }
  });

  subscribe({
    id: newActivityId,
    channelType: channelType.ROOM,
    username: rootState.customer.customerName,
    token: auth.getCurrentToken(),
    callback: (notice) => {
      setNotice(commit, notice);
    },
  });

  if (isSubscribe(oldActivityId, channelType.ROOM)) {
    unSubscribe(oldActivityId, channelType.ROOM);
  }

  getActivityData(newActivityId).then(({ operator, avatarUrl }) => {
    commit('SET_ACTIVITY_OPERATOR', operator);
    commit('SET_ACTIVITY_AVATAR', avatarUrl);
  });
  dispatch('go', 'demo-chat');
}

function sendReplyDeliveredNotification({ activityId, msgs, condition }) {
  const result = condition(activityId, msgs);
  if (result) { replyDelivered(activityId); }
  return result;
}

function countMessagesCount(activityId, msgs) {
  const currentActivityMessages = msgs.filter((msg) => msg.activityId === activityId);
  const operatorMessagesCount = currentActivityMessages.filter((msg) => msg.sender === 'operator').length;
  const customerMessagesCount = currentActivityMessages.filter((msg) => msg.sender === 'customer').length;
  return { operatorMessagesCount, customerMessagesCount };
}

function operatorHasBeenReplied(activityId, msgs) {
  const { operatorMessagesCount, customerMessagesCount } = countMessagesCount(activityId, msgs);
  return customerMessagesCount > 0 && operatorMessagesCount > 1;
}

function newMsgHandling({
  commit,
  dispatch,
  state,
  getters,
  rootGetters,
}, $notice) {
  const notice = $notice;
  if (rootGetters[`home/${focused}`] && notice.sender === 'operator'
    && isSubscribe(state.activityId, channelType.ROOM)) {
    commit('SET_NOTICE', { isRead: true });
    setIsRead(notice.activityId, [notice.id]);
  }
  if (notice.sender === 'operator') {
    commit('SET_OPERATOR_TYPING', false);
    sendReplyDeliveredNotification({
      activityId: state.activityId,
      msgs: getters.chatMsgs,
      condition: () => true,
    });
  }

  if (notice.sender !== 'customer' && (!rootGetters[`home/${focused}`] || state.isHiddenChat)) {
    dispatch('home/newMessagePlaySound', {}, { root: true });
    dispatch('startAutoChangeTitle', i18n.t('T_NEW_MESSAGE'));
  }

  if (!isEmpty(notice.dataModels)) { // replace to notice
    commit('ADD_MENU', notice);
    if (notice.body) commit('ADD_MESSAGE', notice);
  } else commit('ADD_MESSAGE', notice);
  dispatch('go', 'demo-chat');
}

function closeChat({ commit, rootGetters }) {
  commit('CLEAR_STATE_MSGS_AND_ACTIVITY_ID');
  if (rootGetters['app/isUcEnv']) {
    commit('SET_VISIBLE_WIDGET', false);
  }
  commit('CLOSE_CHAT');
  disconnect(); // отключение от сокета
}

function restartChat({
  commit, dispatch,
}) {
  commit('SET_LEFT', false);
  commit('CLEAR_STATE_MSGS_AND_ACTIVITY_ID');
  disconnect();
  dispatch('go', 'first-message'); // поскольку в РБ клиент без логина, с автоклассификатором,
  // то сейчас всегда перенаправляем на first-message
  // TODO!!!: если в РБ будут введены новые экраны (логин и тд) - поменять логику (перенаправлять на нужные страницы)
}

function formatUnsendMsg(msg) {
  return {
    ...msg,
    unableToSend: true,
  };
}

function formatForbiddenToSendMsg(msg) {
  return {
    ...msg,
    forbiddenToSend: true,
  };
}

function updateMessage(commit, notSentMsg, sentMsg) {
  commit('REMOVE_MESSAGE', notSentMsg.temporaryId);
  commit('ADD_MESSAGE', sentMsg);
}

async function onPresenceState({ commit }, notice) {
  const isLength = Object.keys(notice.body).length !== 0;

  Object.keys(notice.body).forEach((key) => {
    const { onlineAt } = notice.body[key].metas[0] || {};
    commit('SET_OPERATOR_ONLINE', { value: isLength, onlineAt });
  });
}

async function onJoin({ commit, state, dispatch }) {
  const activityId = activity.getId();
  const { msgs, virtualOperator } = await getActivityData(activityId);
  if (virtualOperator) {
    commit('SET_VIRTUAL_OPERATOR', true);
  }
  commit('LOAD_MESSAGES', msgs);
  if ((isEmpty(state.activityOperator) || state.activityOperator.id !== '0') && !virtualOperator) {
    commit('SET_VIRTUAL_OPERATOR', false);
    dispatch('markAsRead');
  }
}

async function onFound({ commit }, data) {
  commit('DELETE_SYSTEM_MSGS');
  commit('SET_VIRTUAL_OPERATOR', false);
  commit('SET_ACTIVITY_OPERATOR', data);
  commit('SET_ACTIVITY_AVATAR', data.avatarUrl);
}

async function onPresenceDiff({ commit }, notice) {
  Object.keys(notice.leaves).forEach((key) => {
    if (String(key).indexOf('operator') === 0) {
      const { onlineAt } = notice.leaves[key].metas[0] || {};
      commit('SET_OPERATOR_ONLINE', { value: false, onlineAt });
    }
  });
  Object.keys(notice.joins).forEach((key) => {
    if (String(key).indexOf('operator') === 0) {
      const { onlineAt } = notice.joins[key].metas[0] || {};
      commit('SET_OPERATOR_ONLINE', { value: true, onlineAt });
    }
  });
}

function sendPushWithTimeout(targetFunction) {
  targetFunction()
    .receive('error', () => targetFunction)
    .receive('timeout', () => targetFunction);
}

function handeSetToken(customerToken) {
  auth.setRoleToken(typOfRole, customerToken);
}

function initName(customer) {
  let userName;
  userName = [customer.firstName, customer.middleName, customer.lastName]
    .filter((x) => (x !== null) || (x !== '')).join(' ');
  if (!userName) userName = `Клиент #ID ${customer.id}`;
  return userName;
}

let typingTimeout = null;

// обработка полученного события is_typing (другая сторона диалога печатает)
// производится не чаще чем раз в throttleTimeForReceiveTypingHandler
const throttleTimeForReceiveTypingHandler = 1000;

// через typingPrintTime c момента последней обработки события `is_typing`
// прекратится отображение "печатания" другой стороной диалога в UI
const typingPrintTime = 1500;

// пуш события typing (текущая сторона диалога печатает)
// производится не чаще чем раз в throttleTimeForTypingHandler
const throttleTimeForTypingHandler = 1000;

let typingActivityTimer = null;

function setTypingOff(commit) {
  clearTimeout(typingActivityTimer);
  typingActivityTimer = setTimeout(() => {
    commit('SET_OPERATOR_TYPING', false);
    typingActivityTimer = null;
  }, typingPrintTime);
}

function setTypingOn(commit) {
  commit('SET_OPERATOR_TYPING', true);

  setTypingOff(commit);
}

const onReceiveTypingEvt = throttle(setTypingOn, throttleTimeForReceiveTypingHandler);

function pushTypingEvtWithTimeout() {
  const activityId = activity.getId();
  if (activityId) {
    sendPushWithTimeout(() => pushTypingEvt(activityId, true));
  }
}

const typingActivityTimers = {};

const onTyping = (activityId) => {
  let fn = typingActivityTimers[activityId];
  if (typeof typingActivityTimers[activityId] === 'undefined') {
    fn = throttle(pushTypingEvtWithTimeout, throttleTimeForTypingHandler);
    typingActivityTimers[activityId] = fn;
  }
  return fn(activityId);
};

function filterOutSystemMsgsForOperator(state, msgs) {
  const systemMsgsForOperator = msgs.filter((msg) => msg.sender === 'info_message' && msg.receiver === 'operator');
  const filteredMsgs = difference(msgs, systemMsgsForOperator);
  state.msgs = state.msgs.concat(filteredMsgs);
}

const actions = {
  update({
    commit,
    dispatch,
    getters,
    state,
    rootGetters,
    rootState,
  }, notice) {
    switch (notice.type) {
      case commandType.JOIN:
        onJoin({ commit, state, dispatch });
        break;
      case commandType.PRESENCE_STATE:
        onPresenceState({ commit }, notice);
        break;
      case commandType.PRESENCE_DIFF:
        onPresenceDiff({ commit }, notice);
        break;
      case commandType.NEW_MSG:
        if (!(notice.sender === 'info_message' && notice.receiver === 'operator')) {
          newMsgHandling({
            commit,
            dispatch,
            state,
            getters,
            rootGetters,
          }, notice);
        }
        break;
      case commandType.ERROR:
        break;
      case commandType.LEFT:
        onLeft({ state, dispatch, commit }, notice.data);
        break;
      case commandType.UNABLE_TO_JOIN:
        onUnableToJoin({ dispatch, commit, state });
        break;
      case commandType.TRANSFERED:
        onTransfer({
          commit, dispatch, rootState,
        }, notice.data.newActivityId, notice.data.oldActivityId);
        break;
      case commandType.IS_READ:
        commit('MARK_AS_READ', notice.ids);
        break;
      case commandType.IS_TYPING:
        if (notice.value) onReceiveTypingEvt(commit, notice.activityId);
        break;
      case commandType.FOUND_OPERATOR:
        onFound({ commit, getters, dispatch }, notice.data);
        break;
      default:
        break;
    }
  },
  sendPushWithTimeout,
  go({ commit }, path) {
    commit('GO', path);
  },
  init({ dispatch, rootGetters }) {
    const activityId = activity.getId();
    if (!isEmpty(activityId)) {
      dispatch('go', 'demo-chat');
      const setWidgetInvisible = rootGetters['app/isUcEnv'];
      dispatch('onDemoChatOpen', { setWidgetInvisible });
    }
  },
  async onDemoChatOpen({
    commit, dispatch, state, rootState,
  }, payload = { setWidgetInvisible: false }) {
    const activityId = activity.getId();
    const { customerId } = rootState.customer;
    if (!isEmpty(activityId) && !isEmpty(customerId)
      && !isSubscribe(state.activityId, channelType.ROOM)) {
      const activityData = await getActivityData(activityId);
      const activeStatuses = ['pending', 'in_progress', 'in_hold'];
      if (!activeStatuses.includes(activityData.state)) {
        activity.remove();
        return;
      }
      commit('LOGIN', activityId);
      dispatch('subscribe');
      const {
        msgs, operator, state: status, avatarUrl,
      } = activityData;
      if (!allowedStatuses.includes(status)) {
        onUnableToJoin({ dispatch, commit, state });
      } else {
        dispatch('send', { text: state.firstMsg });
        if (!isEmpty(operator)) { commit('SET_ACTIVITY_OPERATOR', operator); }
        if (!isEmpty(avatarUrl)) { commit('SET_ACTIVITY_AVATAR', avatarUrl); }
        commit('LOAD_MESSAGES', msgs);
        commit('UPDATE_MENU');
        sendReplyDeliveredNotification({
          activityId,
          msgs,
          condition: operatorHasBeenReplied,
        });

        dispatch('markAsRead');
      }
      if (payload.setWidgetInvisible) {
        commit('SET_VISIBLE_WIDGET', false);
      }
      commit('OPEN_CONVERSATION');
    }
  },
  login({ rootState, commit, dispatch }, { username, intent }) {
    onLogin({
      rootState, commit, dispatch, username, intent,
    });
  },
  async updateCustomerName({ rootState }) {
    const { customerId } = rootState.customer;
    const firstName = `customer_${customerId}`;
    const userRole = rootState.user.role;
    return api.updateOmniCustomer({ id: customerId, firstName }, userRole);
  },
  updateCustomerState({ commit, dispatch, rootState }, { username }) {
    commit(customerSET, { username }, { root: true });
    startActivityPage(dispatch, rootState);
  },
  async createActivityByMessage({ commit, dispatch, rootState }, { username, message }) {
    commit(customerSET, { username }, { root: true });

    const sanitizedMessage = hideCard(message);
    commit('SET_FIRSTMSG_UNSEND', false);
    await dispatch('verificationToken');
    return api.createActivity(rootState.app[CHANNEL_NAME]).then(async (id) => {
      activity.setId(id);
      commit('LOGIN', id);
      await dispatch('subscribe'); // TODO нужно проанализировать логику onDemoChatOpen и по возможности объединить методы или убрать лишний фун-ал
      socketEventEmitter.once('socketConnected', async () => {
        await getActivityData(id);
        await dispatch('send', { text: sanitizedMessage }); // Отправка сообщения после подключения сокета
        dispatch('go', 'demo-chat');
        commit('SET_FIRST_MSG', sanitizedMessage);
      });
    })
      .catch((err) => {
        commit('SET_FIRSTMSG_UNSEND', true);
        throw err;
      });
  },

  send({ commit, state, rootState }, data) {
    if (data && !isEmpty(data.text)) {
      const sanitizedText = sanitizeHtml(data.text);
      const msgObj = {
        activityId: state.activityId,
        body: hideCard(sanitizedText),
        insertedAt: new Date(),
        sender: 'customer',
        tag: data.quickReply?.tag,
        temporaryId: generateUniqueId(10),
        username: rootState.customer.customerName,
      };

      const unableToSendMsg = formatUnsendMsg(msgObj);
      try {
        send(state.activityId, msgObj)
          .receive('ok', (msg) => {
            commit('ADD_MESSAGE', msgObj);
            updateMessage(commit, msgObj, msg);
          })
          .receive('error', () => {
            commit('REMOVE_MESSAGE', msgObj.temporaryId);
            commit('ADD_MESSAGE', unableToSendMsg);
          })
          .receive('timeout', () => {
            commit('REMOVE_MESSAGE', msgObj.temporaryId);
            commit('ADD_MESSAGE', unableToSendMsg);
          });
      } catch {
        commit('REMOVE_MESSAGE', msgObj.temporaryId);
        commit('ADD_MESSAGE', unableToSendMsg);
      }
    }
  },
  resend({ commit, rootState }, messages) {
    if (messages) {
      messages.forEach((message) => {
        const msgObj = {
          activityId: message.activityId,
          body: message.message,
          insertedAt: new Date(),
          sender: 'customer',
          tag: message.tag,
          temporaryId: message.temporaryId,
          username: rootState.customer.customerName,
        };

        send(message.activityId, msgObj)
          .receive('ok', (msg) => {
            updateMessage(commit, msgObj, msg);
          })
          .receive('error', () => {
            commit('SET_CONNECTED', false);
          })
          .receive('timeout', () => {
            commit('SET_CONNECTED', false);
          });
      });
    }
  },
  async sendFile({
    commit, dispatch, state, rootState,
  }, file) {
    const temporaryId = generateUniqueId(10);
    const msgObj = {
      temporaryId,
      body: undefined,
      activityId: state.activityId,
      insertedAt: new Date(),
      sender: 'customer',
      username: rootState.customer.customerName,
      progress: 0,
      attachments: [{
        type: 'file',
        preview: undefined,
        title: file.name,
        ext: file.name.slice((Math.max(0, file.name.lastIndexOf('.')) || Infinity) + 1),
        size: file.size,
        file,
      }],
    };

    if (file.size < rootState.app[C_WEB_ATTACHMENT_MAX_SIZE]) {
      commit('ADD_MESSAGE', msgObj);
      commit('ADD_PROGRESS', temporaryId);

      const formData = new FormData();
      formData.append('temporary_id', temporaryId);
      formData.append('activity_id', state.activityId);
      formData.append('attachment', file);

      const xhr = new XMLHttpRequest();
      await dispatch('verificationToken');
      await xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/upload`, true);
      await xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
      xhr.upload.onprogress = (event) => {
        commit('UPDATE_PROGRESS', {
          temporaryId,
          value: (event.loaded / event.total) * 0.99,
        });
      };
      xhr.onload = () => {
        if (xhr.status === 201) {
          commit('UPDATE_PROGRESS', {
            temporaryId,
            value: 1,
          });
        } else {
          commit('UPDATE_PROGRESS', {
            temporaryId,
            value: -1,
          });
        }
        if (xhr.status === 422) {
          commit('REMOVE_MESSAGE', msgObj.temporaryId);
          const forbiddenToSendMsg = formatForbiddenToSendMsg(msgObj);
          commit('ADD_MESSAGE', forbiddenToSendMsg);
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
          });
        }
      };
      xhr.onerror = () => {
        commit('UPDATE_PROGRESS', {
          temporaryId,
          value: -1,
        });
        commit('SET_CONNECTED', false);
      };
      xhr.send(formData);
    } else {
      const msg = {
        infoMessageText: 'size_exceeded',
        error: 413,
      };
      channels.push(state.activityId, commandType.INFO, msg);
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE_IS', [humanFileSize(rootState.app[C_WEB_ATTACHMENT_MAX_SIZE])]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    }
  },
  async resendFile({
    commit, dispatch, state, rootState,
  }, file) {
    if (file.attachments[0].size < rootState.app[C_WEB_ATTACHMENT_MAX_SIZE]) {
      const { temporaryId } = file;

      commit('ADD_PROGRESS', temporaryId);

      const formData = new FormData();
      formData.append('temporary_id', temporaryId);
      formData.append('activity_id', file.activityId);
      formData.append('attachment', file.attachments[0].file);

      const xhr = new XMLHttpRequest();
      await dispatch('verificationToken');
      await xhr.open('POST', `${BACKEND_HTTP_PATH}attachment/upload`, true);
      await xhr.setRequestHeader('Authorization', `Bearer ${auth.getCurrentToken()}`);
      xhr.upload.onprogress = (event) => {
        commit('UPDATE_PROGRESS', {
          temporaryId,
          value: (event.loaded / event.total) * 0.99,
        });
      };
      xhr.onload = () => {
        if (xhr.status === 201) {
          commit('UPDATE_PROGRESS', {
            temporaryId,
            value: 1,
          });
        } else {
          commit('UPDATE_PROGRESS', {
            temporaryId,
            value: -1,
          });
        }
        if (xhr.status === 422) {
          commit('REMOVE_MESSAGE', file.temporaryId);
          const forbiddenToSendMsg = formatForbiddenToSendMsg(file);
          commit('ADD_MESSAGE', forbiddenToSendMsg);
          toast.error({
            title: i18n.t('T_FILE_UPLOADING_ERROR'),
            msg: i18n.t('T_ALLOWED_FILE_TYPE_IS'),
            duration: consts.TOAST_NOTIFICATION_DURATION,
          });
        }
      };
      xhr.onerror = () => {
        commit('UPDATE_PROGRESS', {
          temporaryId,
          value: -1,
        });
        commit('SET_CONNECTED', false);
      };
      xhr.send(formData);
    } else {
      const msg = {
        infoMessageText: 'size_exceeded',
        error: 413,
      };
      channels.push(state.activityId, commandType.INFO, msg);
      toast.error({
        title: i18n.t('T_FILE_UPLOADING_ERROR'),
        msg: i18n.t('T_ALLOWED_FILE_SIZE_IS', [humanFileSize(rootState.app[C_WEB_ATTACHMENT_MAX_SIZE])]),
        duration: consts.TOAST_NOTIFICATION_DURATION,
      });
    }
  },
  markAsRead({ commit, getters, state }) {
    if (!getStateHiddenChat()) {
      const ids = getters.chatMsgs
        .filter((msg) => msg.id && !msg.isRead && (msg.sender === 'operator' || msg.sender === 'info_message'))
        .map((msg) => msg.id);
      commit('SET_READ_MESSAGES', ids);
      const { activityId } = state;
      if (!isEmpty(ids) && activityId
        && isSubscribe(activityId, channelType.ROOM)) {
        clearTimeout(typingTimeout);
        typingTimeout = setTimeout(() => {
          commit('MARK_AS_READ', ids);
          if (isSubscribe(activityId, channelType.ROOM)) {
            sendPushWithTimeout(() => setIsRead(activityId, ids));
          }
        }, 1500);
      }
    }
  },
  onCustomerTyping({ state }) {
    onTyping(state.activityId);
  },
  async openConversation({ commit, dispatch, rootState }) {
    const userid = rootState.customer.customerId;
    commit('OPEN_CONVERSATION');
    dispatch('collapsingChat', false);
    if (userid) {
      await dispatch('verificationToken');
      await api.getCurrentCustomer().then((res) => {
        commit(customerSET, { username: res.firstName, ...res }, { root: true });
      });
      startActivityPage(dispatch, rootState);
    } else if (!rootState.app[C_CUSTOMER_LOGIN_ENABLED]) {
      const temporaryId = generateUniqueId(10);
      commit(customerSET, { username: `customer_${temporaryId}` }, { root: true });
      dispatch('go', 'first-message');
    } else {
      dispatch('go', 'login');
    }
  },
  subscribe({ commit, state, rootState }) {
    subscribe({
      id: state.activityId,
      username: rootState.customer.customerName,
      channelType: channelType.ROOM,
      token: auth.getCurrentToken(),
      callback: (notice) => {
        setNotice(commit, notice);
      },
    });
  },
  baseUnSubscribe({ state, commit }, activityId = state.activityId) {
    if (!isSubscribe(activityId, channelType.ROOM)) return;
    unSubscribe(activityId, channelType.ROOM);
    activity.remove();
    commit('SET_ACTIVITY_OPERATOR', null);
    commit('SET_ACTIVITY_AVATAR', null);
  },
  async unSubscribe({
    commit, state, dispatch, rootState, rootGetters,
  }, options) {
    const activityId = activity.getId() && String(activity.getId());
    dispatch('baseUnSubscribe');
    if (state.page === 'vote') {
      if (options && options.restartChat) {
        restartChat({ commit, dispatch });
      } else {
        dispatch('createVote', { ratingQuality: 0, ratingSpeed: 0 });
      }
    } else if (options && options.withClose) {
      closeChat({ commit, rootState, rootGetters });
    } else if (state.page === 'demo-chat' && !isEmpty(activityId)) {
      const data = await getActivityData(activityId);
      const canceledWithoutProcessing = rootState.app[C_CANCELED_ACTIVITY_WITHOUT_PROCESSING];
      const builtInQualityAssessmentEnable = data.config
        .find((configItem) => configItem.name === 'built_in_quality_assessment_enable').value;
      if ((data.state === 'abandoned' && canceledWithoutProcessing)
        || !builtInQualityAssessmentEnable) {
        closeChat({ commit, rootState, rootGetters });
      } else {
        dispatch('go', 'vote');
      }
    } else if (!options || options.stopChatClosure !== false) {
      closeChat({ commit, rootState, rootGetters });
    }
  },
  unSubscribeWithoutSurvey({
    commit,
    dispatch,
    rootState,
    rootGetters,
  }) {
    dispatch('baseUnSubscribe');
    setTimeout(() => closeChat({ commit, rootState, rootGetters }), 2000);
  },
  logout({ commit, dispatch }) {
    dispatch('baseUnSubscribe');
    disconnect();
    commit('CLEAR_STATE_MSGS_AND_ACTIVITY_ID');
    auth.clearStorage(auth.tokenType.JWT_CUSTOMER);
    dispatch('go', 'login');
    commit('OPEN_CONVERSATION');
  },
  async requestCode({ commit }, { phoneNumber }) {
    try {
      auth.clearStorage(auth.tokenType.JWT_CUSTOMER);
      await api.initAuth('phone', phoneNumber).then((authModel) => {
        commit('SET_AUTH_ID', authModel.id);
      });
      return true;
    } catch (err) {
      throw err;
    }
  },
  async verifyCode({ commit, state }, code) {
    try {
      const res = await api.signIn(state.authId, code);
      localStorage.setItem('authId', state.authId);
      const userFullName = initName(res.customer);
      commit(customerSET, {
        id: res.customer.id,
        username: userFullName,
        isRegistered: true,
      }, { root: true });
      commit('SET_IS_REGISTERED', true);
      commit('SET_CLIENT_TYPE', 'customerSignIn');
      handeSetToken(res.token);
      return res.customer;
    } catch (err) {
      throw err;
    }
  },
  async registerCustomer({ commit }, name) {
    try {
      const res = await api.registrationCustomer(name, phone.get());
      const userFullName = initName(res.customer);
      commit(customerSET, {
        id: res.customer.id,
        username: userFullName,
        isRegistered: res.customer.identified,
      }, { root: true });
      commit('SET_IS_REGISTERED', res.customer.identified);
      handeSetToken(res.token);
      return true;
    } catch (err) {
      throw err;
    }
  },
  async signinAnonymous({ commit }, name) {
    auth.clearStorage(auth.tokenType.JWT_CUSTOMER);
    const res = await api.signinAnon(name);
    commit(customerSET, {
      id: res.customer.id,
      username: res.customer.firstName,
      isRegistered: res.customer.identified,
    }, { root: true });
    commit('SET_IS_REGISTERED', res.customer.identified);
    commit('SET_CLIENT_TYPE', 'customerSigninAnon');
    handeSetToken(res.token);
    return res.customer;
  },
  async signinAdviserAnonymous({ commit }, { customInfo }) {
    auth.clearStorage(auth.tokenType.JWT_CUSTOMER);
    const res = await api.authSigninAdviser(customInfo);
    commit(customerSET, {
      id: res.customer.id,
    }, { root: true });
    commit('SET_CLIENT_TYPE', 'customerAuthSigninAdviser');
    handeSetToken(res.token);
    await api.getCustomer(res.customer.id);
    return res.customer;
  },
  async updateCase({ state, dispatch }, data) {
    await api.updateCase(state.activityId, data);

    if (state.page === 'survey') {
      dispatch('go', 'vote');
    }
  },
  async getRequestTypes() {
    const requestTypes = await api.getAllRequestTypes({ onlyActive: true });
    return addItemsType(requestTypes, typeRequestType);
  },
  async getIntents({ rootState }) {
    const { customerId } = rootState.customer;
    const promise = Promise.all([
      api.getCustomer(customerId),
      api.getAllRequestTypes({ onlyActive: true }),
    ]);
    const [
      customer,
      requestTypes,
    ] = await promise;
    const openCases = sortBy(
      customer.cases,
      (item) => new Date(item.lastNonSystemMessage ? item.lastNonSystemMessage.insertedAt : 0),
    ).reverse();
    return {
      openCases: addItemsType(openCases, typeCase),
      requestTypes: addItemsType(requestTypes, typeRequestType),
    };
  },
  async createVote({
    commit, state, dispatch, rootState, rootGetters,
  }, {
    ratingQuality, ratingSpeed, comment, status, restartChatAfter,
  }) {
    if (status) {
      await api.updateCase(state.activityId, status);
    }
    await api.createVote(state.activityId, ratingQuality, ratingSpeed, comment, status)
      .then(() => {
        if (restartChatAfter) {
          restartChat({ commit, dispatch });
        } else {
          closeChat({ commit, rootState, rootGetters });
          dispatch('go', 'first-message');
          commit('SET_LEFT', false);
        }
      })
      .catch(() => {
        if (restartChatAfter) {
          restartChat({ commit, dispatch });
        } else {
          closeChat({ commit, rootState, rootGetters });
          dispatch('go', 'first-message');
          commit('SET_LEFT', false);
        }
      });
  },
  collapsingChat({ commit, dispatch }, isHidden) {
    sessionStorage.setItem('IS_HIDDEN', isHidden);
    commit('HIDDEN_CHAT', isHidden);
    if (!isHidden) {
      dispatch('stopAutoChangeTitle', {});
    }
  },
  startAutoChangeTitle({ commit }, text) {
    commit('START_AUTO_CHANGE_TITLE', text);
  },
  stopAutoChangeTitle({ commit }) {
    commit('STOP_AUTO_CHANGE_TITLE');
  },
  setOldDocumentTitle({ commit }, title) {
    commit('SET_OLD_DOCUMENT_TITLE', title);
  },
  initializationCustomer({ commit, dispatch }, role) {
    settings.updateHeaderAuthorization(role);
    dispatch('verificationToken');
    return new Promise((resolve) => {
      if (settings.header.authorization) {
        const promise = api.getCurrentCustomer().then((res) => {
          commit(customerSET, { username: res.firstName, ...res }, { root: true });
        }).catch((err) => {
          if (err.message.indexOf(UNAUTHORIZED) > -1) {
            auth.clearStorage(auth.tokenType.JWT_CUSTOMER);
          }
        });
        resolve(promise);
      } else {
        resolve();
      }
    });
  },
  async initMPagination({ commit, rootState }) {
    const activityId = activity.getId();
    const { customerId } = rootState.customer;
    if (activityId) {
      const { msgs, channel } = await getActivityData(activityId);
      let cursorMessage = msgs[0] && msgs[0].id;
      if (!cursorMessage) {
        await api.getCustomerCases(customerId).then((cases) => {
          const lastCase = sortBy(cases, [(item) => (item.lastNonSystemMessage
            ? item.lastNonSystemMessage.insertedAt : '')]);
          cursorMessage = last(lastCase).lastNonSystemMessage
            && +last(lastCase).lastNonSystemMessage.id + 1;
        });
      }
      if (cursorMessage) {
        const cursor = `interactions_messages:${cursorMessage}`;
        const getMsgsByChannelId = rootState.app[C_MESSAGES_FEED_HISTORY_FOR_CURRENT_CHANNEL_ONLY];
        const { pageInfo } = await api.getMessagesByPage({
          cursor,
          limit: 1,
          channelId: getMsgsByChannelId
            ? channel.id : undefined,
        });
        if (pageInfo.cursor) {
          commit('SET_CURSOR', cursor);
          commit('SET_HAS_NEXT_PAGE', true);
        }
      }
    }
  },
  async loadMsgByPage({
    commit,
    dispatch,
    state,
    rootState,
  }) {
    commit('SET_ERROR_FETCHING', false);
    if (state.hasNextPage) {
      commit('SET_FETCHING', true);
      await dispatch('verificationToken');
      const activityId = activity.getId();
      const { channel } = await getActivityData(activityId); // TODO сделать рефакторинг по всему demo.js:
      // один раз сохранять в сторе кастомера состояние
      // единожды запрошенной активности,
      // (или обновлять только по необходимости)
      // а не вызывать в каждом методе
      const getMsgsByChannelId = rootState.app[C_MESSAGES_FEED_HISTORY_FOR_CURRENT_CHANNEL_ONLY];
      await api.getMessagesByPage({
        cursor: state.cursor,
        limit: 50,
        channelId: getMsgsByChannelId
          ? channel.id : undefined,
      }).then((response) => {
        const loadedMsg = response.messages
          .filter((msg) => msg.customerType === 'web')
          .map((msg) => formattedMessage(msg));
        commit('SET_FETCHING', false);
        commit('SET_CURSOR', response.pageInfo.cursor);
        commit('SET_HAS_NEXT_PAGE', response.pageInfo.hasNextPage);
        commit('LOAD_MESSAGES_BY_PAGE', loadedMsg);
      })
        .catch(() => {
          commit('SET_ERROR_FETCHING', true);
        });
    }
  },
  voteFormMount({ commit }) {
    commit('SET_VOTE_FORM_MOUNTED_VALUE', true);
  },
  voteFormDestroy({ commit }) {
    commit('SET_VOTE_FORM_MOUNTED_VALUE', false);
  },
  setDefaultSettings({ commit }) {
    commit('SET_LEFT', false);
  },
  pairAccountUrl(_, customerId) {
    return api.getCustomerTokenLink({ customerId });
  },
  async verificationToken({ state }) {
    const tokens = localStorage.getItem('jwt_customer');
    const tokenVerifyCode = localStorage.getItem('tokenVerifyCode');
    const activityId = activity.getId();
    const customInfo = JSON.parse(localStorage.getItem('customInfo') || '{}');

    if (!tokens) return;

    const isTokenValidFlag = isTokenValid(tokens);
    if (isTokenValidFlag) return;

    let response;
    switch (state.clientType) {
      case 'customerSignIn':
        response = await api.signIn(state.authId, tokenVerifyCode);
        break;
      case 'customerSigninAnon':
        response = await api.signinAnon(activityId);
        break;
      case 'customerAuthSigninAdviser':
        response = await api.authSigninAdviser(customInfo);
        break;
      default:
        throw new Error('Невалидный оператор');
    }

    if (response) {
      await handeSetToken(response.token);
    }
  },

  async closeActivityByCustomer({ dispatch, commit }, code) {
    const customInfo = JSON.parse(localStorage.getItem('customInfo') || '{}');
    const activityId = activity.getId();
    commit('SET_LEFT', true);
    dispatch('clearButtonsMenu');
    // под условиями обработка проблемных случаев без события left
    if (!activityId) {
      dispatch('unSubscribe');
      return false;
    }
    await dispatch('verificationToken');

    return await api.closeActivityByCustomer(activityId).then((res) => {
      if (res.state === 'closed') {
        dispatch('unSubscribe');
      }
    });
  },
  clearButtonsMenu({ commit }) {
    commit('CLEAR_MENU');
  },
  getActivityNotification({ rootState }, event) {
    const channelIdentifier = rootState.app[CHANNEL_NAME];
    return api.getActivityNotification({ event, channelIdentifier });
  },
};

const mutations = {
  OPEN_CONVERSATION(state) {
    state.isOpenConversation = true;
  },
  CLOSE_CHAT(state) {
    state.isOpenConversation = false;
  },
  SET_VISIBLE_WIDGET(state, val) {
    state.isVisibleWidget = val;
  },
  LOGIN(state, activityId) {
    state.activityId = activityId;
    state.timeFreeOperator = 0;
  },
  SET_ACTIVITY_ID(state, activityId) {
    state.activityId = activityId;
  },
  SET_VK_ID(state, vkId) {
    state.vkId = vkId;
  },
  ADD_MESSAGE(state, msg) {
    if (findIndex(state.msgs, (item) => msg.id && item.id === msg.id) === -1) {
      const fMsg = formattedMessage(msg, (state.isRegistered ? 'client' : 'anon'));
      const temp = state.msgs.find((item) => item.temporaryId === msg.temporaryId);
      if (msg.temporaryId && temp) {
        Object.assign(temp, fMsg);
      } else {
        state.msgs.push(fMsg);
      }
    }
  },
  ADD_PROGRESS(state, temporaryId) {
    state.progress[temporaryId] = 0;
    state.msgs.find((msg) => msg.temporaryId === temporaryId).progress = 0;
  },
  UPDATE_PROGRESS(state, { temporaryId, value }) {
    state.progress[temporaryId] = value;
    state.msgs.find((msg) => msg.temporaryId === temporaryId).progress = value;
  },
  REMOVE_MESSAGE(state, temporaryId) {
    if (temporaryId) {
      const index = findIndex(state.msgs, (item) => item.temporaryId === temporaryId);
      if (state.msgs[index] && !state.msgs[index].id) {
        state.msgs.splice(index, 1);
      }
    }
  },
  MARK_AS_READ(state, ids) {
    state.msgs = state.msgs.map((msg) => {
      if (ids.includes(msg.id)) {
        msg.isRead = true;
      }
      return msg;
    });
  },
  DELETE_SYSTEM_MSGS(state) {
    state.msgs = state.msgs.filter((msg) => msg.id || msg.temporaryId);
  },
  CLEAR_STATE_MSGS_AND_ACTIVITY_ID(state) {
    state.activityId = '';
    state.msgs = [];
  },
  LOAD_MESSAGES(state, msgs) {
    const nonSendMessages = differenceBy(state.msgs, msgs, 'id').filter((msg) => msg.temporaryId);
    filterOutSystemMsgsForOperator(state, msgs);
    state.msgs = state.msgs.filter((elem, index, self) => index === findIndex(
      self,
      (item) => item.id && elem.id && item.id === elem.id,
    ));
    state.msgs = [...state.msgs, ...nonSendMessages];
  },
  GO(state, path) {
    state.page = path;
  },
  SET_AUTH_ID(state, authId) {
    state.authId = authId;
  },
  SET_INTENT(state, intent) {
    state.intent = intent;
  },
  SET_NOTICE(state, notice) {
    state.notice = notice;
  },
  SET_OPERATOR_TYPING(state, value) {
    state.operatorIsTyping = value;
  },
  SET_OPERATOR_ONLINE(state, { value, onlineAt }) {
    if ((typeof state.onlineAt === 'undefined') || (state.onlineAt <= onlineAt)) {
      state.onlineAt = onlineAt;
      state.operatorOnline = value;
    }
  },
  SET_ACTIVITY_OPERATOR(state, value) {
    state.activityOperator = value;
  },
  SET_ACTIVITY_AVATAR(state, value) {
    state.avatarUrl = value;
  },
  SET_VIRTUAL_OPERATOR(state, value) {
    state.virtualOperator = value;
  },
  SET_IS_REGISTERED(state, value) {
    state.isRegistered = value;
  },
  SET_READ_MESSAGES(state, value) {
    state.readMessageOnly = state.readMessageOnly.concat(value);
  },
  HIDDEN_CHAT(state, value) {
    state.isHiddenChat = value;
  },
  SET_CURSOR(state, value) {
    state.cursor = value;
  },
  SET_HAS_NEXT_PAGE(state, value) {
    state.hasNextPage = value;
  },
  SET_FETCHING(state, value) {
    state.fetching = value;
  },
  SET_ERROR_FETCHING(state, value) {
    state.errorFetching = value;
  },
  LOAD_MESSAGES_BY_PAGE(state, msgs) {
    filterOutSystemMsgsForOperator(state, msgs);
    state.msgs = state.msgs.filter((elem, index, self) => index === findIndex(self, (item) => item.id === elem.id));
  },
  SET_CONNECTED(state, status) {
    state.customerConnected = status;
  },
  SET_FIRSTMSG_UNSEND(state, status) {
    state.firstMsgUnsend = status;
  },
  START_AUTO_CHANGE_TITLE(state, value) {
    if (state.titleIntervalId) {
      return;
    }
    state.activeTitle = value;
    const { oldTitle, activeTitle } = state;
    state.titleIntervalId = setInterval(() => {
      document.title = document.title === activeTitle ? oldTitle : activeTitle;
      window.parent.postMessage(JSON.stringify({ type: 'NEW_TITLE', data: document.title }), '*');
    }, 1000);
  },
  STOP_AUTO_CHANGE_TITLE(state) {
    if (state.titleIntervalId) {
      clearInterval(state.titleIntervalId);
      state.titleIntervalId = null;
      document.title = state.oldTitle;
      window.parent.postMessage(JSON.stringify({ type: 'NEW_TITLE', data: document.title }), '*');
    }
  },
  SET_OLD_DOCUMENT_TITLE(state, title) {
    state.oldTitle = title;
  },
  SET_VOTE_FORM_MOUNTED_VALUE(state, value) {
    state.voteFormMounted = value;
  },
  SET_LEFT(state, value) {
    state.left = value;
  },
  ADD_MENU(state, value) {
    state.buttonsMenu = value.dataModels.map((item) => {
      if (item.type === 'plain' && item.templateName === 'quickReplies') {
        return item.interactiveOption;
      }
      return '';
    }).filter((n) => n).flat();
    state.buttonsMenuMsgId = value.id;
    localButtonsMenu.set(JSON.stringify(value), state.activityId);
  },
  UPDATE_MENU(state) {
    const buttonsMenu = JSON.parse(localButtonsMenu.get(state.activityId));
    if (!isEmpty(buttonsMenu)) {
      mutations.ADD_MENU(state, buttonsMenu);
    }
  },
  CLEAR_MENU(state) {
    state.buttonsMenu = [];
    state.buttonsMenuMsgId = null;
    localButtonsMenu.remove(state.activityId);
  },
  SET_FIRST_MSG(state, value) {
    state.firstMsg = value;
  },
  SET_CLIENT_TYPE(state, clientType) {
    state.clientType = clientType;
    localStorage.setItem('clientType', clientType);
  },
};

const getters = {
  chatMsgs(state) {
    return sortBy(state.msgs, (msg) => new Date(msg.date));
  },
  intent(state) {
    return state.intent;
  },
};

const store = {
  namespaced: true,
  state: stateData,
  actions,
  mutations,
  getters,
};

export default store;
